import { createFileRoute, redirect } from "@tanstack/react-router";
import { differenceInCalendarDays, format, isValid, parse } from "date-fns";
import { Role } from "@/core/components/auth/auth.types";
import { ErrorComponent } from "@/core/components/ErrorComponent";
import { LoadingList } from "@/core/components/LoadingList";
import { groupMetricsQueryOptions } from "@/core/components/sites/group/performance/groupMetricsQueryOptions";
import { getGroupsQueryOptions } from "@/core/components/sites/propertyQueryOptions";

const MAX_DATE_RANGE_DAYS = 31;

function getDefaultMetricsSearch() {
	const today = format(new Date(), "yyyy-MM-dd");
	return { startDate: today, endDate: today };
}

function parseValidDate(value: unknown): Date | null {
	if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return null;
	}
	const parsed = parse(value, "yyyy-MM-dd", new Date());
	return isValid(parsed) ? parsed : null;
}

export const Route = createFileRoute(
	"/_layout/dashboard/group/$groupId/performance/",
)({
	validateSearch: (search: Record<string, unknown>) => {
		const start = parseValidDate(search.startDate);
		const end = parseValidDate(search.endDate);
		// Fall back to defaults on empty / malformed / reversed / oversized
		// ranges — the same ranges the date picker prohibits.
		if (
			!start ||
			!end ||
			differenceInCalendarDays(end, start) < 0 ||
			differenceInCalendarDays(end, start) >= MAX_DATE_RANGE_DAYS
		) {
			return getDefaultMetricsSearch();
		}
		return {
			startDate: search.startDate as string,
			endDate: search.endDate as string,
		};
	},
	beforeLoad: ({ context: { auth } }) => {
		if (auth.isExpired()) {
			return;
		}
		if (!auth.isAuthenticated) {
			throw redirect({
				to: "/",
			});
		}
		if (
			auth.user?.role === Role["Front-Desk"] ||
			auth.user?.role === Role["Editor"] ||
			auth.user?.role === Role["Blogger"]
		) {
			throw redirect({
				to: "/dashboard",
			});
		}
	},
	loaderDeps: ({ search }) => ({
		startDate: search.startDate,
		endDate: search.endDate,
	}),
	loader: ({ context: { queryClient, auth }, params: { groupId }, deps }) => {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		queryClient.ensureQueryData(getGroupsQueryOptions(auth.token!));
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		queryClient.ensureQueryData(
			groupMetricsQueryOptions(
				auth.token!,
				groupId,
				deps.startDate,
				deps.endDate,
				[],
			),
		);
	},
	pendingComponent: LoadingList,
	errorComponent: ErrorComponent,
});
